import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBackendAuth } from '../auth/BackendAuthProvider.jsx';
import { changeLanguage, supportedLanguages } from '../../i18n';

const SettingsDropdown = () => {
  const { logout, refreshData, smmParameters } = useBackendAuth();
  const { t, i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [languageExpanded, setLanguageExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setLanguageExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    setLanguageExpanded(false);
    logout();
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
  };

  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  const currentLanguage = supportedLanguages.find(l => l.code === i18n.language) || supportedLanguages[0];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Gear Icon Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) setLanguageExpanded(false);
        }}
        className="p-2 text-brand-headerText hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-50 rounded"
        aria-label={t('labels.settings')}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-brand-contentBg border border-gray-300 rounded-sm shadow-lg z-50">
          <div className="py-1">
            {/* Language Selector - Collapsible */}
            <button
              onClick={() => setLanguageExpanded(!languageExpanded)}
              className="w-full text-left px-4 py-2 text-sm text-brand-darkText hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{t('labels.language')}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs text-gray-500 mr-2">{currentLanguage.nativeName}</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${languageExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Language Options (expanded) */}
            {languageExpanded && (
              <div className="bg-gray-50">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full text-left pl-11 pr-4 py-1.5 text-sm transition-colors flex items-center justify-between ${
                      i18n.language === lang.code
                        ? 'text-brand-primary font-medium'
                        : 'text-brand-darkText hover:bg-gray-100'
                    }`}
                  >
                    <span>{lang.nativeName}</span>
                    {i18n.language === lang.code && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Company Info */}
            <div className="px-4 py-2 text-sm text-brand-darkText flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span>{t('labels.company')}</span>
              </div>
              <span className="text-xs text-gray-500 font-medium">{smmParameters?.mserp_dataareaid || t('labels.na')}</span>
            </div>

            {/* Refresh Data */}
            <button
              onClick={handleRefreshData}
              disabled={refreshing}
              className="w-full text-left px-4 py-2 text-sm text-brand-darkText hover:bg-gray-100 transition-colors flex items-center"
            >
              <svg
                className={`w-4 h-4 mr-3 text-gray-500 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {refreshing ? t('actions.refreshing') : t('actions.refreshData')}
            </button>

            {/* Divider */}
            <div className="border-t border-gray-200 my-1"></div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-brand-darkText hover:bg-gray-100 transition-colors flex items-center"
            >
              <svg
                className="w-4 h-4 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('actions.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsDropdown;
